/**
 * MealSlotStep
 *
 * Step 1 of the Generate Week wizard.
 * Allows user to select which meal slots (B/L/D per day) to include.
 * Matches Mealime-style layout with icons and toggle buttons.
 */

import React, { useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius } from '../../../styles/theme';
import { useTranslation } from 'react-i18next';
import { MealType } from '../../../redux/mealPlanSlice';
import { toIsoDate } from '../../../utils/localDate';

interface MealSlotStepProps {
  weekStartDate: string;
  selectedSlots: Set<string>;
  onToggleSlot: (slotKey: string) => void;
}

interface DayInfo {
  date: string;
  dayName: string;
  dayNumber: number;
  monthName: string;
}

const MEAL_TYPES: { key: MealType; labelKey: string; icon: keyof typeof Feather.glyphMap }[] = [
  { key: 'breakfast', labelKey: 'thisWeek.mealTypes.breakfast', icon: 'sunrise' },
  { key: 'lunch', labelKey: 'thisWeek.mealTypes.lunch', icon: 'sun' },
  { key: 'dinner', labelKey: 'thisWeek.mealTypes.dinner', icon: 'moon' },
];

export const MealSlotStep: React.FC<MealSlotStepProps> = ({
  weekStartDate,
  selectedSlots,
  onToggleSlot,
}) => {
  const { t, i18n } = useTranslation();
  // Generate 7 days starting from weekStartDate
  const weekDays: DayInfo[] = useMemo(() => {
    const days: DayInfo[] = [];
    const startDate = new Date(weekStartDate + 'T12:00:00');

    for (let i = 0; i < 7; i++) {
      const d = new Date(startDate.getTime());
      d.setDate(d.getDate() + i);

      days.push({
        date: toIsoDate(d),
        dayName: d.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'long' }),
        dayNumber: d.getDate(),
        monthName: d.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { month: 'long' }),
      });
    }

    return days;
  }, [weekStartDate]);

  const createSlotKey = (date: string, mealType: MealType): string => {
    return `${date}-${mealType}`;
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
    >
      {/* Header */}
      <Text style={styles.header}>
        {t('wizard.mealSlotQuestion')}
      </Text>

      {/* Day Rows */}
      {weekDays.map((day) => (
        <View key={day.date} style={styles.dayRow}>
          {/* Day Label */}
          <Text style={styles.dayLabel}>
            {day.dayName}, {day.monthName} {day.dayNumber}
          </Text>

          {/* Meal Buttons */}
          <View style={styles.mealButtons}>
            {MEAL_TYPES.map(({ key, labelKey, icon }) => {
              const slotKey = createSlotKey(day.date, key);
              const isSelected = selectedSlots.has(slotKey);

              return (
                <TouchableOpacity
                  key={key}
                  style={[
                    styles.mealButton,
                    isSelected && styles.mealButtonSelected,
                  ]}
                  onPress={() => onToggleSlot(slotKey)}
                  activeOpacity={0.7}
                >
                  <Feather
                    name={icon}
                    size={20}
                    color={isSelected ? Paprika.background : Paprika.textSecondary}
                  />
                  <Text
                    style={[
                      styles.mealButtonLabel,
                      isSelected && styles.mealButtonLabelSelected,
                    ]}
                  >
                    {t(labelKey)}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      ))}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  content: {
    padding: PaprikaSpacing.lg,
    paddingBottom: PaprikaSpacing.xl,
  },

  // Header
  header: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    textAlign: 'center',
    marginBottom: PaprikaSpacing.xl,
    lineHeight: 28,
  },

  // Day Row
  dayRow: {
    marginBottom: PaprikaSpacing.lg,
  },
  dayLabel: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
    fontWeight: '600',
    marginBottom: PaprikaSpacing.sm,
  },

  // Meal Buttons
  mealButtons: {
    flexDirection: 'row',
    gap: PaprikaSpacing.sm,
  },
  mealButton: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.xs,
    borderRadius: PaprikaRadius.md,
    borderWidth: 1,
    borderColor: Paprika.border,
    backgroundColor: Paprika.background,
    gap: 4,
  },
  mealButtonSelected: {
    backgroundColor: Paprika.textPrimary,
    borderColor: Paprika.textPrimary,
  },
  mealButtonLabel: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
  },
  mealButtonLabelSelected: {
    color: Paprika.background,
  },
});

export default MealSlotStep;
